import { z } from "zod/v4-mini";

import { type PumpDeviceBrand, pumpDeviceBrandSchema } from "@/models/MedicalDeviceModel.ts";
import type { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

export const conditionNameSchema = z.literal(["diabetes"]);
export type ConditionName = z.infer<typeof conditionNameSchema>;
export type ConditionType = "type1" | "type2" | "gestational";
export const conditionTypeSchema: Schema<ConditionType> = z.literal(["type1", "type2", "gestational"], "form.required");
export type MedicationSchema = "noInsulin" | "insulinPen" | "insulinPump";
export const medicationSchemeSchema = z.literal(["noInsulin", "insulinPen", "insulinPump"], "form.required");

export type Condition = {
  periodStart: string;
  periodEnd?: string;
  name: ConditionName;
  diabetesType: ConditionType;
  medicationSchema: MedicationSchema;
  termDate?: string;
  isBillable: boolean;
  pumpModel?: PumpDeviceBrand;
};

export const conditionSchema: Schema<Condition> = cz.camelCaseObject({
  period_start: z.iso.date(),
  period_end: cz.optional(z.iso.date()),
  name: conditionNameSchema,
  diabetes_type: conditionTypeSchema,
  medication_schema: medicationSchemeSchema,
  term_date: cz.optional(z.iso.date()),
  is_billable: z.boolean(),
  pump_model: cz.optional(pumpDeviceBrandSchema),
});

export const diabetesTypeTranslationMapping = (
  type: ConditionType | undefined,
  insulin: MedicationSchema | undefined,
): string => {
  switch (true) {
    case type === "type1":
      return "models.diabetesType.type1";
    case type === "type2":
      return "models.diabetesType.type2";
    case type === "gestational" && insulin === undefined:
      return "models.diabetesType.gestational";
    case type === "gestational" && insulin === "noInsulin":
      return "models.diabetesType.gestationalNoInsulin";
    case type === "gestational" && (insulin === "insulinPen" || insulin === "insulinPump"):
      return "models.diabetesType.gestationalWithInsulin";
    default:
      return "models.diabetesType.unknown";
  }
};

export const medicationSchemaTranslationMapping = (schema: MedicationSchema): string => {
  switch (schema) {
    case "noInsulin":
      return "models.medicationSchema.noInsulin";
    case "insulinPen":
      return "models.medicationSchema.insulinPen";
    case "insulinPump":
      return "models.medicationSchema.insulinPump";
  }
};

export const diabetesTypeColorMapping = (type: ConditionType, insulin: MedicationSchema): PaletteColors => {
  switch (true) {
    case type === "type1":
      return "yellow";
    case type === "type2":
      return "blue";
    case type === "gestational" && insulin === "noInsulin":
      return "purple";
    default:
      return "pink";
  }
};

export const medicationSchemaColorMapping = (schema: MedicationSchema): PaletteColors => {
  switch (schema) {
    case "noInsulin":
      return "yellow";
    case "insulinPen":
      return "blue";
    case "insulinPump":
      return "green";
  }
};
